import React, { useState, useEffect, useCallback } from 'react';
import Footer from './common/Footer';
import Sidebar from './Sidebar';
import userService from '../services/userService';

const ROLE_META = {
  SUPEROWNER: { label:'Superowner', avBg:'#1B3B5F', avColor:'#D4AF37', rbBg:'#1B3B5F', rbColor:'#D4AF37', rbBorder:'#D4AF37' },
  OWNER:      { label:'Owner',      avBg:'#FFF3CD', avColor:'#856404', rbBg:'#FFF3CD', rbColor:'#856404', rbBorder:'#F0D070' },
  ADMIN:      { label:'Admin',      avBg:'#D0E4F7', avColor:'#1B3B5F', rbBg:'#D0E4F7', rbColor:'#1B3B5F', rbBorder:'#A8CAEC' },
  ACCOUNTS:   { label:'Accounts',   avBg:'#EDEDFF', avColor:'#5B52C5', rbBg:'#EDEDFF', rbColor:'#5B52C5', rbBorder:'#C8C0F0' },
  INVENTORY:  { label:'Inventory',  avBg:'#D4EDDA', avColor:'#2D5F4F', rbBg:'#D4EDDA', rbColor:'#2D5F4F', rbBorder:'#A8D8BC' },
  SECURITY:   { label:'Security',   avBg:'#FDDEDE', avColor:'#DC3545', rbBg:'#FDDEDE', rbColor:'#DC3545', rbBorder:'#F8BBBB' },
  FACULTY:    { label:'Faculty',    avBg:'#D0E4F7', avColor:'#1B3B5F', rbBg:'#D0E4F7', rbColor:'#1B3B5F', rbBorder:'#A8CAEC' },
  STUDENT:    { label:'Student',    avBg:'#FFF3CD', avColor:'#856404', rbBg:'#FFF3CD', rbColor:'#856404', rbBorder:'#F0D070' },
};
const meta = (role) => ROLE_META[role?.toUpperCase()] || ROLE_META.ADMIN;

const ROLE_HINTS = {
  ADMIN:    'Full operational access: registrations, admissions, room allocation, inventory masters, payment collection. Cannot edit fee structures.',
  ACCOUNTS: 'Finance module only: fee collection, pay card management, security deposits, and financial reports. No operational access.',
  INVENTORY:'Can raise purchase and consumption requisitions and view stock levels. Cannot add Item or Supplier masters.',
  SECURITY: 'Upload biometric dumps and view the live attendance dashboard only.',
};

const MATRIX = [
  {section:'Registration & Admission'},
  {label:'View Registration Pool',       owner:true, admin:true,  accounts:false,inventory:false,security:false},
  {label:'Approve Admission',            owner:true, admin:true,  accounts:false,inventory:false,security:false},
  {label:'Generate Student ID',          owner:true, admin:true,  accounts:false,inventory:false,security:false},
  {section:'Room Allocation'},
  {label:'Allocate / Change Bed',        owner:true, admin:true,  accounts:false,inventory:false,security:false},
  {label:'View Room Occupancy',          owner:true, admin:true,  accounts:true, inventory:false,security:true},
  {section:'Finance'},
  {label:'Edit Fee Structure',           owner:'★',  admin:false, accounts:false,inventory:false,security:false},
  {label:'Collect Payment',              owner:true, admin:true,  accounts:true, inventory:false,security:false},
  {label:'Manage Pay Card',              owner:true, admin:true,  accounts:true, inventory:false,security:false},
  {label:'Manage Security Deposit',      owner:true, admin:true,  accounts:true, inventory:false,security:false},
  {label:'View Financial Reports',       owner:true, admin:true,  accounts:true, inventory:false,security:false},
  {section:'Inventory'},
  {label:'Add Item Master',              owner:true, admin:true,  accounts:false,inventory:false,security:false},
  {label:'Add Supplier Master',          owner:true, admin:true,  accounts:false,inventory:false,security:false},
  {label:'Purchase Requisition',         owner:true, admin:true,  accounts:false,inventory:true, security:false},
  {label:'Consumption Requisition',      owner:true, admin:true,  accounts:false,inventory:true, security:false},
  {label:'View Stock Levels',            owner:true, admin:true,  accounts:false,inventory:true, security:false},
  {section:'Attendance & Leaves'},
  {label:'Upload Biometric Dump',        owner:true, admin:true,  accounts:false,inventory:false,security:true},
  {label:'View Attendance Dashboard',    owner:true, admin:true,  accounts:false,inventory:false,security:true},
  {label:'Approve Leave Applications',   owner:true, admin:true,  accounts:false,inventory:false,security:false},
  {section:'Notifications'},
  {label:'Send Manual Notifications',    owner:true, admin:true,  accounts:false,inventory:false,security:false},
  {label:'Configure Notification Rules', owner:'★',  admin:false, accounts:false,inventory:false,security:false},
  {section:'User Management'},
  {label:'Add / Edit Staff Users',       owner:'★',  admin:false, accounts:false,inventory:false,security:false},
  {label:'Deactivate / Activate Users',  owner:'★',  admin:false, accounts:false,inventory:false,security:false},
  {label:'Reset Staff Password',         owner:'★',  admin:false, accounts:false,inventory:false,security:false},
];

const PCell = ({val}) => {
  const base = {display:'inline-flex',alignItems:'center',justifyContent:'center',width:28,height:28,borderRadius:'50%',fontWeight:700,fontSize:13};
  if (val==='★') return <span style={{...base,background:'#FFF3CD',color:'#856404'}}>★</span>;
  if (val===true) return <span style={{...base,background:'#D4EDDA',color:'#2D5F4F'}}>✓</span>;
  return <span style={{...base,background:'#E8E4DF',color:'#C4BEB8'}}>–</span>;
};

const Overlay = ({children,onClose}) => (
  <div onClick={e=>e.target===e.currentTarget&&onClose()}
    style={{position:'fixed',inset:0,background:'rgba(13,31,45,0.65)',zIndex:1000,display:'flex',alignItems:'center',justifyContent:'center',backdropFilter:'blur(2px)'}}>
    {children}
  </div>
);
const ModalBox = ({children,style}) => (
  <div style={{background:'white',width:'90%',maxWidth:560,maxHeight:'90vh',overflowY:'auto',position:'relative',boxShadow:'0 40px 100px rgba(0,0,0,0.3)',...style}}>
    {children}
  </div>
);
const FormGroup = ({label,children}) => (
  <div style={{marginBottom:24}}>
    <label style={{display:'block',fontSize:11,fontWeight:700,textTransform:'uppercase',letterSpacing:1,color:'#6B6B6B',marginBottom:10}}>{label}</label>
    {children}
  </div>
);
const FInput = ({value,onChange,placeholder,type='text'}) => (
  <input type={type} value={value} onChange={e=>onChange(e.target.value)} placeholder={placeholder}
    style={{width:'100%',padding:'14px 0',border:'none',borderBottom:'1px solid #E8E4DF',background:'transparent',fontSize:15,fontFamily:"'Work Sans',sans-serif",color:'#2C2C2C',outline:'none'}}/>
);
const FSelect = ({value,onChange,children}) => (
  <select value={value} onChange={e=>onChange(e.target.value)}
    style={{width:'100%',padding:'14px 0',border:'none',borderBottom:'1px solid #E8E4DF',background:'transparent',fontSize:15,fontFamily:"'Work Sans',sans-serif",color:'#2C2C2C',outline:'none',cursor:'pointer'}}>
    {children}
  </select>
);

const S = {
  close:   {position:'absolute',top:18,right:20,background:'none',border:'none',fontSize:20,color:'#C4BEB8',cursor:'pointer',lineHeight:1},
  mHead:   {fontFamily:"'Cormorant Garamond',serif",fontSize:30,fontWeight:400,color:'#1B3B5F',marginBottom:6,margin:0},
  mSub:    {fontSize:14,color:'#6B6B6B',marginBottom:32,marginTop:6},
  err:     {background:'rgba(232,93,74,0.08)',borderLeft:'4px solid #E85D4A',padding:'12px 16px',marginBottom:20,fontSize:13,color:'#E85D4A'},
  btnNavy: {background:'#1B3B5F',color:'white',border:'none',padding:'13px 24px',fontWeight:700,fontSize:13,letterSpacing:1,textTransform:'uppercase',cursor:'pointer',fontFamily:"'Work Sans',sans-serif"},
  btnGold: {background:'#D4AF37',color:'#1B3B5F',border:'none',padding:'13px 24px',fontWeight:700,fontSize:13,letterSpacing:1,textTransform:'uppercase',cursor:'pointer',fontFamily:"'Work Sans',sans-serif"},
  btnGhost:{background:'transparent',color:'#6B6B6B',border:'1px solid #E8E4DF',padding:'13px 24px',fontWeight:700,fontSize:13,letterSpacing:1,textTransform:'uppercase',cursor:'pointer',fontFamily:"'Work Sans',sans-serif"},
  btnDanger:{background:'#DC3545',color:'white',border:'none',padding:'13px 24px',fontWeight:700,fontSize:13,letterSpacing:1,textTransform:'uppercase',cursor:'pointer',fontFamily:"'Work Sans',sans-serif"},
  btnNavySm:{background:'#1B3B5F',color:'white',border:'none',padding:'8px 16px',fontSize:11,fontWeight:700,letterSpacing:.5,textTransform:'uppercase',cursor:'pointer',fontFamily:"'Work Sans',sans-serif"},
  btnGhostSm:{background:'transparent',color:'#6B6B6B',border:'1px solid #E8E4DF',padding:'8px 16px',fontSize:11,fontWeight:700,letterSpacing:.5,textTransform:'uppercase',cursor:'pointer',fontFamily:"'Work Sans',sans-serif"},
  btnGoldSm:{background:'#D4AF37',color:'#1B3B5F',border:'none',padding:'8px 16px',fontSize:11,fontWeight:700,letterSpacing:.5,textTransform:'uppercase',cursor:'pointer',fontFamily:"'Work Sans',sans-serif"},
};

/* ───── PAGINATION (matches Admission screen style) ───── */
const Pagination = ({ page, total, perPage, onPage, onPerPage }) => {
  const totalPages = Math.ceil(total / perPage);
  if (total === 0) return null;
  const start = page * perPage + 1;
  const end   = Math.min((page + 1) * perPage, total);
  const pages = Array.from({ length: totalPages }, (_, i) => i);
  const Btn = ({ label, onClick, disabled, active }) => (
    <button onClick={onClick} disabled={disabled} style={{
      padding:'7px 11px', border: active ? 'none' : '1px solid #E8E4DF', borderRadius: 4, minWidth: 32,
      background: active ? '#1B3B5F' : disabled ? '#F8F6F3' : '#fff',
      color: active ? '#fff' : disabled ? '#ccc' : '#2C2C2C',
      cursor: disabled ? 'default' : 'pointer', fontSize: 13, fontWeight: active ? 700 : 400,
    }}>{label}</button>
  );
  return (
    <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'20px 40px', borderTop:'1px solid #E8E4DF', background:'#fff' }}>
      <div style={{ display:'flex', gap:6, alignItems:'center' }}>
        <Btn label="«" onClick={()=>onPage(0)} disabled={page===0}/>
        <Btn label="‹" onClick={()=>onPage(page-1)} disabled={page===0}/>
        {pages.map(i => <Btn key={i} label={i+1} onClick={()=>onPage(i)} active={i===page}/>)}
        <Btn label="›" onClick={()=>onPage(page+1)} disabled={page>=totalPages-1}/>
        <Btn label="»" onClick={()=>onPage(totalPages-1)} disabled={page>=totalPages-1}/>
      </div>
      <div style={{ display:'flex', alignItems:'center', gap:8, fontSize:13, color:'#6B6B6B' }}>
        <span>Showing {start}–{end} of {total} &nbsp;|&nbsp; Per page</span>
        {[10,20,30,50].map(s => (
          <button key={s} onClick={()=>{ if(onPerPage){ onPerPage(s); onPage(0); } }} style={{
            padding:'5px 11px', border:'none', borderRadius:4, cursor:'pointer', fontSize:13, fontWeight:600,
            background: perPage===s ? '#D4AF37' : '#F0EDE8', color: perPage===s ? '#fff' : '#888',
          }}>{s}</button>
        ))}
      </div>
    </div>
  );
};

export default function UserManagementScreen({onNavigate,onLogout}) {
  const [users,     setUsers]     = useState([]);
  const [userPage,   setUserPage]   = useState(0);
  const [userPerPage, setUserPerPage] = useState(10);
  const [loading,   setLoading]   = useState(true);
  const [activeTab, setActiveTab] = useState('staff');
  const [search,    setSearch]    = useState('');
  const [fRole,     setFRole]     = useState('');
  const [fStat,     setFStat]     = useState('');
  const [toast,     setToast]     = useState(null);

  const [addModal,  setAddModal]  = useState(false);
  const [editModal, setEditModal] = useState(false);
  const [deactModal,setDeactModal]= useState(false);
  const [pwModal,   setPwModal]   = useState(false);
  const [target,    setTarget]    = useState(null);

  const [addForm,  setAddForm]  = useState({firstName:'',lastName:'',mobile:'',email:'',role:'',password:'',confirm:''});
  const [addErr,   setAddErr]   = useState('');
  const [addBusy,  setAddBusy]  = useState(false);

  const [editForm, setEditForm] = useState({firstName:'',lastName:'',username:'',email:'',mobile:'',role:'',enabled:true,newPassword:''});
  const [editErr,  setEditErr]  = useState('');
  const [editBusy, setEditBusy] = useState(false);

  const [newPw,   setNewPw]   = useState('');
  const [pwErr,   setPwErr]   = useState('');
  const [pwBusy,  setPwBusy]  = useState(false);

  const currentUser = JSON.parse(localStorage.getItem('user')||'{}');

  const load = useCallback(async()=>{
    try{ setLoading(true); setUsers(await userService.getAll()); }
    catch{ showToast('Failed to load users','error'); }
    finally{ setLoading(false); }
  },[]);

  useEffect(()=>{ load(); },[load]);

  const showToast=(msg,type='success')=>{ setToast({msg,type}); setTimeout(()=>setToast(null),3500); };

  const counts = users.reduce((a,u)=>{
    const r=u.role?.toUpperCase(); a[r]=(a[r]||0)+1; a.TOTAL++; return a;
  },{TOTAL:0,OWNER:0,SUPEROWNER:0,ADMIN:0,ACCOUNTS:0,INVENTORY:0,SECURITY:0});

  // reset to page 1 when search/filter changes
  const filtered = users.filter(u=>{
    const q=search.toLowerCase();
    return (!q||u.username?.toLowerCase().includes(q)||u.email?.toLowerCase().includes(q)||u.mobile?.includes(q))
      && (!fRole||u.role?.toUpperCase()===fRole)
      && (!fStat||(fStat==='ACTIVE'?u.enabled:!u.enabled));
  });

  // ADD
  const handleAdd = async()=>{
    setAddErr('');
    const {firstName,lastName,mobile,email,role,password,confirm} = addForm;
    if(!firstName||!lastName) return setAddErr('Please enter the full name.');
    if(!mobile)               return setAddErr('Mobile number is required — it is the login credential.');
    if(!role)                 return setAddErr('Please assign a role to this user.');
    if(!password)             return setAddErr('Please set a temporary password.');
    if(password!==confirm)    return setAddErr('Passwords do not match.');
    if(password.length<6)     return setAddErr('Password must be at least 6 characters.');
    setAddBusy(true);
    try{
      await userService.create({firstName,lastName,mobile,email,role,password});
      showToast(`Account created for ${firstName} ${lastName}`);
      setAddModal(false);
      setAddForm({firstName:'',lastName:'',mobile:'',email:'',role:'',password:'',confirm:''});
      load();
    }catch(e){ setAddErr(e.message||'Failed to create user.'); }
    finally{ setAddBusy(false); }
  };

  // EDIT
  const openEdit=(u)=>{ setTarget(u); setEditForm({firstName:u.firstName||'',lastName:u.lastName||'',username:u.username,email:u.email,mobile:u.mobile||'',role:u.role,enabled:u.enabled,newPassword:''}); setEditErr(''); setEditModal(true); };
  const handleEdit=async()=>{
    setEditErr('');
    if(!editForm.username) return setEditErr('Username cannot be empty.');
    setEditBusy(true);
    try{ await userService.update(target.id,editForm); showToast('User updated'); setEditModal(false); load(); }
    catch(e){ setEditErr(e.message||'Failed to update.'); }
    finally{ setEditBusy(false); }
  };

  // PASSWORD
  const openPw=(u)=>{ setTarget(u); setNewPw(''); setPwErr(''); setPwModal(true); };
  const handlePw=async()=>{
    setPwErr('');
    if(newPw.length<6) return setPwErr('Password must be at least 6 characters.');
    setPwBusy(true);
    try{ await userService.changePassword(target.id,newPw); showToast('Password changed'); setPwModal(false); }
    catch(e){ setPwErr(e.message||'Failed.'); }
    finally{ setPwBusy(false); }
  };

  // TOGGLE
  const openDeact=(u)=>{ setTarget(u); setDeactModal(true); };
  const confirmToggle=async()=>{
    try{ await userService.toggleStatus(target.id); showToast(target.enabled?`${target.username} deactivated`:`${target.username} activated`); setDeactModal(false); load(); }
    catch{ showToast('Failed to update status','error'); }
  };

  const fmtDate=d=>{ if(!d) return '—'; return new Date(d).toLocaleDateString('en-IN',{day:'2-digit',month:'short',year:'numeric'}); };

  return (
    <div style={{display:'flex',minHeight:'100vh',background:'#F8F6F3',fontFamily:"'Work Sans',sans-serif"}}>
      <Sidebar activeItem="users" onNavigate={onNavigate} onLogout={onLogout}/>

      <main style={{marginLeft:280,flex:1}}>

        {/* Top bar */}
        <div style={{background:'white',padding:'22px 40px',borderBottom:'1px solid #E8E4DF',position:'sticky',top:0,zIndex:50,boxShadow:'0 2px 12px rgba(0,0,0,0.04)'}}>
          <div style={{fontSize:12,color:'#6B6B6B',marginBottom:8,display:'flex',alignItems:'center',gap:6}}>
            <span style={{color:'#D4AF37',cursor:'pointer'}}>Settings</span>
            <span style={{color:'#C4BEB8'}}>/</span>
            <span>User Management</span>
          </div>
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'center'}}>
            <h1 style={{fontFamily:"'Cormorant Garamond',serif",fontSize:32,fontWeight:400,color:'#1B3B5F',margin:0}}>User Management</h1>
            <button onClick={()=>{setAddErr('');setAddForm({firstName:'',lastName:'',mobile:'',email:'',role:'',password:'',confirm:''});setAddModal(true);}}
              style={S.btnGold}>+ Add New User</button>
          </div>
        </div>

        <div style={{padding:'36px 40px'}}>

          {/* Notice */}
          <div style={{background:'#FFFBEE',borderLeft:'4px solid #D4AF37',padding:'14px 20px',marginBottom:28,display:'flex',alignItems:'center',gap:12,fontSize:13,color:'#856404'}}>
            🔐 <span><strong>Students</strong> and <strong>Parents</strong> are managed via the Admission module and Student Profile respectively. This module manages staff accounts only.</span>
          </div>

          {/* Stats */}
          <div style={{display:'grid',gridTemplateColumns:'repeat(6,1fr)',gap:18,marginBottom:32}}>
            {[
              {label:'Total Staff', val:counts.TOTAL,     bg:'#1B3B5F',numC:'white',     lblC:'rgba(255,255,255,0.6)',border:'#C4BEB8'},
              {label:'Owners',      val:(counts.OWNER||0)+(counts.SUPEROWNER||0), bg:'white',numC:'#1B3B5F',lblC:'#6B6B6B',border:'#D4AF37'},
              {label:'Admin',       val:counts.ADMIN||0,  bg:'white',numC:'#1B3B5F',lblC:'#6B6B6B',border:'#1B3B5F'},
              {label:'Accounts',    val:counts.ACCOUNTS||0,bg:'white',numC:'#1B3B5F',lblC:'#6B6B6B',border:'#6C63FF'},
              {label:'Inventory',   val:counts.INVENTORY||0,bg:'white',numC:'#1B3B5F',lblC:'#6B6B6B',border:'#2D5F4F'},
              {label:'Security',    val:counts.SECURITY||0,bg:'white',numC:'#1B3B5F',lblC:'#6B6B6B',border:'#E85D4A'},
            ].map(s=>(
              <div key={s.label} style={{background:s.bg,padding:'22px 18px',boxShadow:'0 2px 12px rgba(0,0,0,0.05)',borderTop:`3px solid ${s.border}`,textAlign:'center',transition:'transform .2s'}}>
                <div style={{fontFamily:"'Cormorant Garamond',serif",fontSize:40,fontWeight:500,color:s.numC,lineHeight:1,marginBottom:6}}>{loading?'—':s.val}</div>
                <div style={{fontSize:10,fontWeight:700,textTransform:'uppercase',letterSpacing:1.5,color:s.lblC}}>{s.label}</div>
              </div>
            ))}
          </div>

          {/* Tabs */}
          <div style={{display:'flex',borderBottom:'2px solid #E8E4DF',marginBottom:28}}>
            {[['staff','Staff Directory'],['matrix','Privilege Matrix']].map(([id,lbl])=>(
              <div key={id} onClick={()=>setActiveTab(id)}
                style={{padding:'14px 28px',fontSize:12,fontWeight:700,textTransform:'uppercase',letterSpacing:1.5,cursor:'pointer',
                  color:activeTab===id?'#1B3B5F':'#6B6B6B',
                  borderBottom:activeTab===id?'3px solid #D4AF37':'3px solid transparent',marginBottom:-2}}>
                {lbl}
              </div>
            ))}
          </div>

          {/* ── STAFF DIRECTORY ── */}
          {activeTab==='staff' && (<>
            {/* Toolbar */}
            <div style={{background:'white',padding:'18px 24px',boxShadow:'0 2px 12px rgba(0,0,0,0.05)',display:'flex',alignItems:'center',gap:14,marginBottom:2,flexWrap:'wrap'}}>
              <div style={{flex:1,minWidth:200,position:'relative'}}>
                <svg width="16" height="16" fill="none" stroke="#C4BEB8" strokeWidth="2" viewBox="0 0 24 24"
                  style={{position:'absolute',left:14,top:'50%',transform:'translateY(-50%)'}}>
                  <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/>
                </svg>
                <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search by name, email or mobile…"
                  style={{width:'100%',padding:'10px 14px 10px 40px',border:'1px solid #E8E4DF',background:'#F8F6F3',fontSize:14,fontFamily:"'Work Sans',sans-serif",outline:'none'}}/>
              </div>
              <select value={fRole} onChange={e=>setFRole(e.target.value)}
                style={{padding:'10px 14px',border:'1px solid #E8E4DF',background:'#F8F6F3',fontSize:13,fontFamily:"'Work Sans',sans-serif",outline:'none',cursor:'pointer'}}>
                <option value="">All Roles</option>
                <option value="SUPEROWNER">Superowner</option>
                <option value="OWNER">Owner</option>
                <option value="ADMIN">Admin</option>
                <option value="ACCOUNTS">Accounts</option>
                <option value="INVENTORY">Inventory</option>
                <option value="SECURITY">Security</option>
              </select>
              <select value={fStat} onChange={e=>setFStat(e.target.value)}
                style={{padding:'10px 14px',border:'1px solid #E8E4DF',background:'#F8F6F3',fontSize:13,fontFamily:"'Work Sans',sans-serif",outline:'none',cursor:'pointer'}}>
                <option value="">All Status</option>
                <option value="ACTIVE">Active</option>
                <option value="INACTIVE">Inactive</option>
              </select>
            </div>

            {/* Table */}
            <div style={{background:'white',boxShadow:'0 2px 12px rgba(0,0,0,0.05)',overflowX:'auto'}}>
              <table style={{width:'100%',borderCollapse:'collapse'}}>
                <thead>
                  <tr style={{background:'#F8F6F3',borderBottom:'2px solid #E8E4DF'}}>
                    {['User','Mobile','Role','Login Method','Status','Last Login','Actions'].map(h=>(
                      <th key={h} style={{padding:'13px 20px',textAlign:'left',fontSize:10,fontWeight:700,textTransform:'uppercase',letterSpacing:1.5,color:'#6B6B6B',whiteSpace:'nowrap'}}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {loading?(
                    <tr><td colSpan={7} style={{padding:48,textAlign:'center',color:'#6B6B6B',fontSize:14}}>Loading…</td></tr>
                  ):filtered.length===0?(
                    <tr><td colSpan={7} style={{padding:48,textAlign:'center',color:'#6B6B6B',fontSize:14}}>No users match your search criteria.</td></tr>
                  ):filtered.slice(userPage*userPerPage, (userPage+1)*userPerPage).map(u=>{
                    const m=meta(u.role);
                    const displayName=(u.firstName||u.lastName)?`${u.firstName||''} ${u.lastName||''}`.trim():u.username; const initials=(displayName||'?').slice(0,2).toUpperCase();
                    const isSelf=u.username===currentUser.username;
                    return (
                      <tr key={u.id} style={{borderBottom:'1px solid #E8E4DF'}}>
                        {/* User */}
                        <td style={{padding:'16px 20px',verticalAlign:'middle'}}>
                          <div style={{display:'flex',alignItems:'center',gap:14}}>
                            <div style={{width:44,height:44,borderRadius:'50%',display:'flex',alignItems:'center',justifyContent:'center',fontWeight:700,fontSize:15,flexShrink:0,background:m.avBg,color:m.avColor}}>{initials}</div>
                            <div>
                              <div style={{fontWeight:600,color:'#2C2C2C',marginBottom:2}}>{(u.firstName||u.lastName)?`${u.firstName||''} ${u.lastName||''}`.trim():u.username}</div>
                              <div style={{fontSize:12,color:'#6B6B6B'}}>{u.email}</div>
                            </div>
                          </div>
                        </td>
                        {/* Mobile */}
                        <td style={{padding:'16px 20px',fontSize:14,color:'#2C2C2C',verticalAlign:'middle'}}>{u.mobile||'—'}</td>
                        {/* Role */}
                        <td style={{padding:'16px 20px',verticalAlign:'middle'}}>
                          <span style={{display:'inline-block',padding:'4px 14px',fontSize:11,fontWeight:700,textTransform:'uppercase',letterSpacing:.5,borderRadius:4,background:m.rbBg,color:m.rbColor,border:`1px solid ${m.rbBorder||m.rbColor+'44'}`}}>{m.label}</span>
                        </td>
                        {/* Login method */}
                        <td style={{padding:'16px 20px',fontSize:12,color:'#6B6B6B',verticalAlign:'middle'}}>🔒 Password + OTP</td>
                        {/* Status */}
                        <td style={{padding:'16px 20px',verticalAlign:'middle'}}>
                          <span style={{display:'inline-flex',alignItems:'center',gap:6,padding:'5px 14px',fontSize:11,fontWeight:700,textTransform:'uppercase',letterSpacing:.5,borderRadius:0,background:u.enabled?'#D4EDDA':'#F8D7DA',color:u.enabled?'#155724':'#721C24'}}>
                            <span style={{width:8,height:8,borderRadius:'50%',background:u.enabled?'#28a745':'#DC3545'}}/>
                            {u.enabled?'Active':'Inactive'}
                          </span>
                        </td>
                        {/* Created */}
                        <td style={{padding:'16px 20px',fontSize:13,color:'#6B6B6B',verticalAlign:'middle'}}>{fmtDate(u.createdAt)}</td>
                        {/* Actions */}
                        <td style={{padding:'16px 20px',verticalAlign:'middle'}}>
                          <div style={{display:'flex',gap:8,alignItems:'center'}}>
                            <button onClick={()=>openEdit(u)} style={{...S.btnNavySm,padding:'7px 18px',fontSize:11,fontWeight:700,letterSpacing:.8,textTransform:'uppercase'}}>EDIT</button>
                            {u.enabled?(
                              <button onClick={()=>!isSelf&&openDeact(u)} disabled={isSelf}
                                style={{...S.btnGhostSm,cursor:isSelf?'not-allowed':'pointer',opacity:isSelf?.5:1,padding:'7px 18px',fontSize:11,fontWeight:700,letterSpacing:.8,textTransform:'uppercase'}}
                                title={isSelf?'Cannot deactivate yourself':''}>DEACTIVATE</button>
                            ):(
                              <button onClick={()=>openDeact(u)} style={{...S.btnGoldSm,padding:'7px 18px',fontSize:11,fontWeight:700,letterSpacing:.8,textTransform:'uppercase'}}>ACTIVATE</button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              <Pagination page={userPage} total={filtered.length} perPage={userPerPage} onPage={setUserPage} onPerPage={setUserPerPage}/>
            </div>
          </>)}

          {/* ── PRIVILEGE MATRIX ── */}
          {activeTab==='matrix' && (
            <div style={{background:'white',boxShadow:'0 2px 12px rgba(0,0,0,0.05)',overflow:'hidden'}}>
              <div style={{padding:'24px 30px',borderBottom:'2px solid #E8E4DF',display:'flex',alignItems:'center',justifyContent:'space-between'}}>
                <div style={{fontFamily:"'Cormorant Garamond',serif",fontSize:22,fontWeight:600,color:'#1B3B5F'}}>Role Privilege Matrix</div>
                <div style={{fontSize:12,color:'#6B6B6B'}}>Students &amp; Parents are not staff — see Admission Module &amp; Student Profile</div>
              </div>
              <div style={{overflowX:'auto'}}>
                <table style={{width:'100%',borderCollapse:'collapse',minWidth:720}}>
                  <thead>
                    <tr style={{background:'#1B3B5F'}}>
                      {['Permission','Owner','Admin','Accounts','Inventory','Security'].map((h,i)=>(
                        <th key={h} style={{padding:'14px 16px',fontSize:11,fontWeight:700,textTransform:'uppercase',letterSpacing:1,
                          color:i===0?'white':'rgba(255,255,255,0.75)',textAlign:i===0?'left':'center',
                          borderRight:'1px solid rgba(255,255,255,0.08)',minWidth:i===0?220:undefined}}>
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {MATRIX.map((row,i)=>{
                      if(row.section) return (
                        <tr key={i}>
                          <td colSpan={6} style={{background:'linear-gradient(135deg,#0D1F2D,#1B3B5F)',color:'rgba(255,255,255,0.6)',fontSize:10,fontWeight:700,textTransform:'uppercase',letterSpacing:2,padding:'9px 24px',border:'none'}}>
                            {row.section}
                          </td>
                        </tr>
                      );
                      return (
                        <tr key={i} style={{borderBottom:'1px solid #E8E4DF'}}>
                          <td style={{padding:'13px 24px',textAlign:'left',fontWeight:500,color:'#2C2C2C',background:'#F8F6F3',borderRight:'1px solid #E8E4DF',fontSize:13}}>{row.label}</td>
                          {['owner','admin','accounts','inventory','security'].map(col=>(
                            <td key={col} style={{padding:'13px 16px',textAlign:'center',borderRight:'1px solid #E8E4DF'}}><PCell val={row[col]}/></td>
                          ))}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {/* Legend */}
              <div style={{display:'flex',gap:24,flexWrap:'wrap',padding:'16px 24px',background:'#F8F6F3',borderTop:'1px solid #E8E4DF',fontSize:12,color:'#6B6B6B'}}>
                {[{val:true,text:'Has access'},{val:false,text:'No access'},{val:'★',text:'Owner only — restricted privilege'}].map(l=>(
                  <div key={l.text} style={{display:'flex',alignItems:'center',gap:8}}><PCell val={l.val}/>{l.text}</div>
                ))}
              </div>
            </div>
          )}
        </div>
      <Footer />
      </main>

      {/* ══ ADD USER MODAL ══ */}
      {addModal && (
        <Overlay onClose={()=>setAddModal(false)}>
          <ModalBox>
            <div style={{height:4,background:'linear-gradient(90deg,#1B3B5F,#D4AF37)'}}/>
            <button onClick={()=>setAddModal(false)} style={S.close}>✕</button>
            <div style={{padding:40}}>
              <h2 style={S.mHead}>Add New User</h2>
              <p style={S.mSub}>Create a staff account. A WhatsApp message with login details will be sent to the registered mobile.</p>
              {addErr&&<div style={S.err}>{addErr}</div>}
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:20}}>
                <FormGroup label="First Name"><FInput placeholder="e.g. Priya" value={addForm.firstName} onChange={v=>setAddForm(f=>({...f,firstName:(v=>v.replace(/(?:^|\s)\S/g,c=>c.toUpperCase()))(v)}))}/></FormGroup>
                <FormGroup label="Last Name"><FInput placeholder="e.g. Nair" value={addForm.lastName} onChange={v=>setAddForm(f=>({...f,lastName:(v=>v.replace(/(?:^|\s)\S/g,c=>c.toUpperCase()))(v)}))}/></FormGroup>
              </div>
              <FormGroup label="Mobile Number (Login Credential)"><FInput placeholder="+91 XXXXX XXXXX" value={addForm.mobile} onChange={v=>setAddForm(f=>({...f,mobile:v}))}/></FormGroup>
              <FormGroup label="Email Address"><FInput placeholder="user@omkarahostel.in" value={addForm.email} onChange={v=>setAddForm(f=>({...f,email:v}))}/></FormGroup>
              <FormGroup label="Assign Role">
                <FSelect value={addForm.role} onChange={v=>setAddForm(f=>({...f,role:v}))}>
                  <option value="">— Select Role —</option>
                  <option value="ADMIN">Admin</option>
                  <option value="ACCOUNTS">Accounts / Finance</option>
                  <option value="INVENTORY">Inventory / Store Manager</option>
                  <option value="SECURITY">Security / Biometrics Operator</option>
                </FSelect>
                {addForm.role&&ROLE_HINTS[addForm.role]&&(
                  <div style={{marginTop:10,padding:'12px 16px',background:'#FFFBEE',borderLeft:'3px solid #D4AF37',fontSize:12,color:'#856404',lineHeight:1.6}}>
                    🔐 {ROLE_HINTS[addForm.role]}
                  </div>
                )}
              </FormGroup>
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:20}}>
                <FormGroup label="Set Password"><FInput type="password" placeholder="Temporary password" value={addForm.password} onChange={v=>setAddForm(f=>({...f,password:v}))}/></FormGroup>
                <FormGroup label="Confirm Password"><FInput type="password" placeholder="Repeat password" value={addForm.confirm} onChange={v=>setAddForm(f=>({...f,confirm:v}))}/></FormGroup>
              </div>
              <div style={{display:'flex',gap:12,marginTop:32,paddingTop:24,borderTop:'1px solid #E8E4DF'}}>
                <button onClick={()=>setAddModal(false)} style={{...S.btnGhost,flex:1}}>Cancel</button>
                <button onClick={handleAdd} disabled={addBusy} style={{...S.btnGold,flex:2}}>{addBusy?'Creating…':'Create Account'}</button>
              </div>
            </div>
          </ModalBox>
        </Overlay>
      )}

      {/* ══ EDIT USER MODAL ══ */}
      {editModal&&target&&(
        <Overlay onClose={()=>setEditModal(false)}>
          <ModalBox>
            <div style={{height:4,background:'linear-gradient(90deg,#1B3B5F,#D4AF37)'}}/>
            <button onClick={()=>setEditModal(false)} style={S.close}>✕</button>
            <div style={{padding:40}}>
              <h2 style={S.mHead}>Edit — {target?(target.firstName||target.lastName?`${target.firstName||''} ${target.lastName||''}`.trim():target.username):''}</h2>
              <p style={S.mSub}>Update details, change role, or reset the user's password.</p>
              {editErr&&<div style={S.err}>{editErr}</div>}
              {/* First + Last name row */}
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:24,marginBottom:24}}>
                <FormGroup label="First Name"><FInput value={editForm.firstName} onChange={v=>setEditForm(f=>({...f,firstName:(v=>v.replace(/(?:^|\s)\S/g,c=>c.toUpperCase()))(v)}))}/></FormGroup>
                <FormGroup label="Last Name"><FInput value={editForm.lastName} onChange={v=>setEditForm(f=>({...f,lastName:(v=>v.replace(/(?:^|\s)\S/g,c=>c.toUpperCase()))(v)}))}/></FormGroup>
              </div>
              <FormGroup label="Mobile Number"><FInput value={editForm.mobile} onChange={v=>setEditForm(f=>({...f,mobile:v}))}/></FormGroup>
              <FormGroup label="Email Address"><FInput value={editForm.email} onChange={v=>setEditForm(f=>({...f,email:v}))}/></FormGroup>
              <FormGroup label="Role">
                <FSelect value={editForm.role} onChange={v=>setEditForm(f=>({...f,role:v}))}>
                  <option value="OWNER">Owner</option>
                  <option value="ADMIN">Admin</option>
                  <option value="ACCOUNTS">Accounts / Finance</option>
                  <option value="INVENTORY">Inventory / Store Manager</option>
                  <option value="SECURITY">Security / Biometrics Operator</option>
                </FSelect>
              </FormGroup>
              {/* Reset password inline */}
              <div style={{marginBottom:24}}>
                <label style={{display:'block',fontSize:11,fontWeight:700,textTransform:'uppercase',letterSpacing:1,color:'#6B6B6B',marginBottom:10}}>
                  RESET PASSWORD <span style={{fontWeight:400,textTransform:'none',letterSpacing:0,fontSize:12,color:'#9B9B9B'}}>(leave blank to keep current)</span>
                </label>
                <input
                  type="password"
                  placeholder="New password (optional)"
                  value={editForm.newPassword}
                  onChange={e=>setEditForm(f=>({...f,newPassword:e.target.value}))}
                  style={{width:'100%',padding:'14px 0',border:'none',borderBottom:'1px solid #E8E4DF',background:'transparent',fontSize:15,fontFamily:"'Work Sans',sans-serif",color:'#2C2C2C',outline:'none',boxSizing:'border-box'}}
                />
              </div>
              <div style={{display:'flex',gap:12,marginTop:32,paddingTop:24,borderTop:'1px solid #E8E4DF'}}>
                <button onClick={()=>setEditModal(false)} style={{...S.btnGhost,flex:1,padding:'14px',fontSize:11,fontWeight:700,letterSpacing:1,textTransform:'uppercase'}}>CANCEL</button>
                <button onClick={handleEdit} disabled={editBusy} style={{...S.btnNavy,flex:2,padding:'14px',fontSize:11,fontWeight:700,letterSpacing:1,textTransform:'uppercase'}}>{editBusy?'Saving…':'SAVE CHANGES'}</button>
              </div>
            </div>
          </ModalBox>
        </Overlay>
      )}

      {/* ══ CHANGE PASSWORD MODAL ══ */}
      {pwModal&&target&&(
        <Overlay onClose={()=>setPwModal(false)}>
          <ModalBox style={{maxWidth:420}}>
            <div style={{height:4,background:'linear-gradient(90deg,#1B3B5F,#D4AF37)'}}/>
            <button onClick={()=>setPwModal(false)} style={S.close}>✕</button>
            <div style={{padding:40}}>
              <h2 style={S.mHead}>Reset Password</h2>
              <p style={S.mSub}>Set a new password for <strong>{target.username}</strong>.</p>
              {pwErr&&<div style={S.err}>{pwErr}</div>}
              <FormGroup label="New Password"><FInput type="password" placeholder="Min 6 characters" value={newPw} onChange={setNewPw}/></FormGroup>
              <div style={{display:'flex',gap:12,marginTop:32,paddingTop:24,borderTop:'1px solid #E8E4DF'}}>
                <button onClick={()=>setPwModal(false)} style={{...S.btnGhost,flex:1}}>Cancel</button>
                <button onClick={handlePw} disabled={pwBusy} style={{...S.btnNavy,flex:2}}>{pwBusy?'Saving…':'Set Password'}</button>
              </div>
            </div>
          </ModalBox>
        </Overlay>
      )}

      {/* ══ DEACTIVATE / ACTIVATE MODAL ══ */}
      {deactModal&&target&&(
        <Overlay onClose={()=>setDeactModal(false)}>
          <ModalBox style={{maxWidth:420}}>
            <div style={{height:4,background:target.enabled?'#DC3545':'#D4AF37'}}/>
            <button onClick={()=>setDeactModal(false)} style={S.close}>✕</button>
            <div style={{padding:40,textAlign:'center'}}>
              <div style={{fontSize:52,marginBottom:16}}>{target.enabled?'⚠️':'✅'}</div>
              <div style={{fontFamily:"'Cormorant Garamond',serif",fontSize:26,fontWeight:400,color:'#1B3B5F',marginBottom:8}}>
                {target.enabled?'Deactivate User?':'Reactivate User?'}
              </div>
              <div style={{fontSize:14,color:'#6B6B6B',lineHeight:1.7}}>{target.enabled?'You are about to deactivate':'You are about to reactivate'}</div>
              <div style={{fontWeight:700,color:'#2C2C2C',fontSize:15,margin:'6px 0 16px'}}>{target.username}</div>
              <div style={{fontSize:14,color:'#6B6B6B',lineHeight:1.7}}>
                {target.enabled
                  ?'This user will lose all portal access immediately. Their records and history will be preserved. You can reactivate them at any time.'
                  :'This user will regain access to the portal immediately.'}
              </div>
              <div style={{display:'flex',justifyContent:'center',gap:12,marginTop:28,paddingTop:24,borderTop:'1px solid #E8E4DF'}}>
                <button onClick={()=>setDeactModal(false)} style={S.btnGhost}>Cancel</button>
                <button onClick={confirmToggle} style={target.enabled?S.btnDanger:S.btnGold}>
                  {target.enabled?'Yes, Deactivate':'Yes, Activate'}
                </button>
              </div>
            </div>
          </ModalBox>
        </Overlay>
      )}

      {/* Toast */}
      {toast&&(
        <div style={{position:'fixed',bottom:32,right:32,background:toast.type==='error'?'#E85D4A':'#1B3B5F',color:'white',padding:'14px 24px',fontSize:14,fontWeight:600,boxShadow:'0 8px 32px rgba(0,0,0,0.2)',zIndex:9999}}>
          {toast.type==='error'?'✕ ':'✓ '}{toast.msg}
        </div>
      )}
    </div>
  );
}